import { useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Dialog } from '../ui/Dialog/Dialog';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Checkbox } from '../atoms/Checkbox';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { useTranslation } from '../../i18n/useTranslation';
import { ThemeContext } from '../../theme/ThemeContext';
import { ActiveAquariumContext } from '../../contexts/AquariumContext';
import { fetchAquariums } from '../../services/aquariumService';
import { useOnboarding } from './OnboardingContext';
import { usePriorityNoticeActive } from '../../contexts/PriorityNoticeContext';
import {
  SCREENSHOT_HEIGHT,
  SCREENSHOT_WIDTH,
  screenshotUrl,
  tryItPath as slideTryItPath,
  whatsNewCopyKey,
} from './slides';
import './OnboardingTour.css';

type AquariumState = 'loading' | 'error' | 'none' | 'some';

/** Copy of the last slide per aquarium state (loading: the CTA is disabled). */
const START_ACTION: Record<AquariumState, { body: string; cta: string }> = {
  loading: { body: 'onboarding.slides.start.bodyLoading', cta: 'onboarding.slides.start.ctaLoading' },
  error: { body: 'onboarding.slides.start.bodyUnknown', cta: 'onboarding.slides.start.ctaUnknown' },
  none: { body: 'onboarding.slides.start.bodyNoAquarium', cta: 'onboarding.slides.start.ctaNoAquarium' },
  some: { body: 'onboarding.slides.start.bodyWithAquarium', cta: 'onboarding.slides.start.ctaWithAquarium' },
};

/** Focus is in a field where the arrow keys move the caret or change a value. */
function isTextEntry(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true;
  if (el instanceof HTMLInputElement) return !['checkbox', 'radio', 'button', 'submit', 'reset'].includes(el.type);
  return el.getAttribute('role') === 'slider';
}

/**
 * The onboarding tour: a modal Dialog (focus trap, Escape = skip, focus restore)
 * with one outcome per slide — screenshot, headline, one or two sentences and
 * an optional "Try it now" link into the feature. Back/Next, "Step x of n" plus
 * step dots, "Skip" on every slide, the left/right arrow keys outside text
 * fields, and a "Show again next time" checkbox (the only thing that changes the
 * stored preference). Rendered once by OnboardingProvider's host in App.tsx.
 *
 * "What's new" (existing users after a big update, see OnboardingContext): the
 * same dialog with only the new slides, its own title and intro, "Done" on the
 * last slide and "Show the full tour" to switch to every slide.
 */
export function OnboardingTour() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const onboarding = useOnboarding();
  const { isOpen: requestedOpen, close, showOnStart, setShowOnStart, saveFailed, saving, loadState, slides, whatsNew } = onboarding;
  // Never stacked on a priority notice (privacy policy update): the tour waits
  // until that notice is closed, then opens.
  const priorityNoticeActive = usePriorityNoticeActive();
  const isOpen = requestedOpen && !priorityNoticeActive;
  const theme = useContext(ThemeContext)?.theme ?? 'dark';
  const activeAquariumId = useContext(ActiveAquariumContext)?.activeAquariumId ?? null;
  // Whether the user has an aquarium comes from the settled aquarium list itself
  // (same query and cache as ActiveAquariumDataProvider), not from the derived
  // context: there an empty list means "none", "still loading" and "failed" alike,
  // so the tour could not tell a new user from a list that has not arrived yet.
  const aquariumsQuery = useQuery({ queryKey: ['aquariums'], queryFn: fetchAquariums });
  const aquariumList = aquariumsQuery.data;
  const aquariumState: AquariumState =
    aquariumList !== undefined
      ? aquariumList.length > 0 ? 'some' : 'none'
      : aquariumsQuery.isError ? 'error' : 'loading';
  // The active aquarium when it is in the list, otherwise the first one.
  const aquariumId =
    aquariumState === 'some' && aquariumList !== undefined
      ? (aquariumList.find((a) => a.id === activeAquariumId) ?? aquariumList[0]).id
      : null;

  const [index, setIndex] = useState(0);
  const navigatedRef = useRef(false);
  const headingId = useId();
  const total = slides.length;
  const slide = slides[Math.min(index, total - 1)];
  const isLast = index === total - 1;
  const language = i18n.language;

  // Every opening — and a switch from "What's new" to the full tour — starts at the first slide.
  useEffect(() => {
    if (isOpen) {
      setIndex(0);
      navigatedRef.current = false;
    }
  }, [isOpen, slides]);

  // After Back/Next (not on open — the Dialog focuses itself then) focus moves to
  // the new slide's heading, so screen readers read the new content.
  useEffect(() => {
    if (!isOpen || !navigatedRef.current) return;
    document.getElementById(headingId)?.focus();
  }, [index, isOpen, headingId]);

  // Only the current and the next screenshot are requested.
  useEffect(() => {
    if (!isOpen || isLast) return;
    const next = slides[index + 1];
    const img = new Image();
    img.src = screenshotUrl(next, language, theme);
  }, [index, isOpen, isLast, language, theme, slides]);

  const go = useCallback(
    (delta: number) => {
      setIndex((i) => {
        const nextIndex = Math.max(0, Math.min(total - 1, i + delta));
        if (nextIndex !== i) navigatedRef.current = true;
        return nextIndex;
      });
    },
    [total],
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const dialog = document.querySelector('[data-testid="onboarding-tour"]');
      const active = document.activeElement;
      if (!dialog || (active && active !== document.body && !dialog.contains(active))) return;
      if (isTextEntry(active)) return;
      event.preventDefault();
      go(event.key === 'ArrowRight' ? 1 : -1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, go]);

  const finishTo = (path: string): void => {
    close();
    navigate(path);
  };

  const keyBase = `onboarding.slides.${slide.id}`;
  const whatsNewKey = whatsNewCopyKey(slides);
  const tryItPath = slideTryItPath(slide, aquariumId);
  const start = START_ACTION[aquariumState];
  const startPath =
    aquariumState === 'some' && aquariumId !== null
      ? `/aquariums/${encodeURIComponent(aquariumId)}/measurements/new`
      : aquariumState === 'error' ? '/aquariums' : '/aquariums/new';
  const body = slide.id === 'start' ? t(start.body) : t(`${keyBase}.body`);

  const footer = (
    <div className="rg-onboarding__footer">
      {whatsNew && isLast ? (
        <Button variant="ghost" onClick={onboarding.open}>
          {t('onboarding.whatsNew.fullTour')}
        </Button>
      ) : (
        <Button variant="ghost" onClick={close}>
          {isLast ? t('onboarding.finish') : t('onboarding.skip')}
        </Button>
      )}
      <div className="rg-onboarding__actions">
        {index > 0 && (
          <Button variant="secondary" onClick={() => go(-1)}>
            {t('onboarding.back')}
          </Button>
        )}
        {whatsNew && isLast ? (
          <Button variant="primary" onClick={close}>
            {t('onboarding.whatsNew.done')}
          </Button>
        ) : isLast ? (
          <Button
            variant="primary"
            isLoading={aquariumState === 'loading'}
            disabled={aquariumState === 'loading'}
            onClick={() => finishTo(startPath)}
            data-testid="onboarding-start-cta"
          >
            {t(start.cta)}
          </Button>
        ) : (
          <Button variant="primary" onClick={() => go(1)}>
            {t('onboarding.next')}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={close}
      title={whatsNew ? t(`${whatsNewKey}.dialogTitle`) : t('onboarding.dialogTitle')}
      size="lg"
      footer={footer}
      data-testid="onboarding-tour"
    >
      <div className="rg-onboarding">
        {whatsNew && <Text className="rg-onboarding__intro">{t(`${whatsNewKey}.intro`)}</Text>}
        <div className="rg-onboarding__progress">
          <LiveRegion as="span" className="rg-onboarding__progress-text">
            {t('onboarding.progress', { current: index + 1, total })}
          </LiveRegion>
          <ol className="rg-onboarding__dots" aria-hidden="true">
            {slides.map((s, i) => (
              <li
                key={s.id}
                className={`rg-onboarding__dot${i === index ? ' rg-onboarding__dot--current' : ''}${i < index ? ' rg-onboarding__dot--done' : ''}`}
              />
            ))}
          </ol>
        </div>

        <div
          className="rg-onboarding__slide"
          key={slide.id}
          role="group"
          aria-labelledby={headingId}
          data-testid={`onboarding-slide-${slide.id}`}
        >
          <div className="rg-onboarding__text">
            <Heading level={3} id={headingId} tabIndex={-1} className="rg-onboarding__title">
              {t(`${keyBase}.title`)}
            </Heading>
            <Text className="rg-onboarding__body">{body}</Text>
            {tryItPath !== null && (
              <Link to={tryItPath} className="rg-onboarding__try" onClick={close}>
                {t('onboarding.tryIt')}
              </Link>
            )}
          </div>
          <figure className="rg-onboarding__figure">
            <img
              className="rg-onboarding__image"
              src={screenshotUrl(slide, language, theme)}
              alt={t(`${keyBase}.imageAlt`)}
              width={SCREENSHOT_WIDTH}
              height={SCREENSHOT_HEIGHT}
              decoding="async"
            />
          </figure>
        </div>

        {/* Outside the slide content, on every slide. In the (scrolling) body, so the
            body always has a tab stop on small screens (WCAG 2.1.1). */}
        <Checkbox
          className="rg-onboarding__show-again"
          label={t('onboarding.showOnStart')}
          checked={showOnStart !== false}
          disabled={loadState === 'loading'}
          aria-busy={saving || undefined}
          onChange={(event) => void setShowOnStart(event.target.checked)}
        />
        {saveFailed && (
          <Alert tone="danger" compact className="rg-onboarding__alert">
            {t('onboarding.saveFailed')}
          </Alert>
        )}
      </div>
    </Dialog>
  );
}

export default OnboardingTour;
